import React, { useEffect } from "react";
import { Link, useParams, Navigate } from "react-router-dom";
import { ArrowLeft, ArrowRight, Check, Sparkles, Shield } from "lucide-react";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import { useLang } from "@/contexts/LanguageContext";

const ACCENTS = {
  urban: "#7ec043",
  lighting: "#d4a249",
  heating: "#e07b3c",
};

const BROCHURE_IMAGES = {
  urban: "/images/brochure-urban.jpg",
  lighting: "/images/brochure-lighting.png",
  heating: "/images/brochure-heating.png",
};

const BROCHURE_DOWNLOADS = {
  urban: [
    {
      label: "EMKA Urban Catalog (Part 1)",
      href: "https://customer-assets.emergentagent.com/job_emka-landing/artifacts/xd4w7wpq_EMKA%20METAL%20URBAN.pdf",
      sizeMb: 32,
    },
    {
      label: "EMKA Urban Catalog (Part 2)",
      href: "https://customer-assets.emergentagent.com/job_emka-landing/artifacts/u9rqbauo_EMKA%20METAL%20URBAN%202.pdf",
      sizeMb: 84,
    },
  ],
  lighting: [
    { label: "EMKA Lighting Catalog", href: "/images/brochure-lighting.png" },
  ],
  heating: [
    { label: "EMKA Heating Catalog", href: "/images/brochure-heating.png" },
  ],
};

const BrochurePage = ({ slug: propSlug }) => {
  const params = useParams();
  const slug = propSlug || params.slug;
  const { t } = useLang();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [slug]);

  if (!t.brochures || !t.brochures[slug]) {
    return <Navigate to="/" replace />;
  }

  const b = t.brochures[slug];
  const accent = ACCENTS[slug] || "var(--emka-gold)";

  return (
    <div
      data-testid={`brochure-page-${slug}`}
      className="min-h-screen bg-emka"
    >
      <Header />

      {/* Hero */}
      <section
        className="pt-[150px] lg:pt-[170px] pb-10"
        data-testid="brochure-hero"
      >
        <div className="max-w-[1200px] mx-auto px-4 sm:px-6 lg:px-10">
          <Link
            to="/"
            className="inline-flex items-center gap-2 text-white/75 hover:text-white text-xs font-semibold tracking-widest uppercase mb-5"
          >
            <ArrowLeft size={14} />
            {t.category.backHome}
          </Link>
          <p
            className="text-[12px] font-bold tracking-[0.32em] uppercase"
            style={{ color: accent }}
          >
            {b.eyebrow}
          </p>
          <h1
            className="mt-3 font-display font-extrabold text-white leading-tight"
            style={{ fontSize: "clamp(2rem, 4vw, 3rem)" }}
          >
            {b.title}
          </h1>
          <p className="mt-3 text-white/75 max-w-2xl">{b.tagline}</p>
          <span
            className="mt-5 inline-block h-[3px] w-14"
            style={{ backgroundColor: accent }}
          />
        </div>
      </section>

      {/* Brochure image */}
      {BROCHURE_IMAGES[slug] && (
        <section className="pb-4" data-testid="brochure-image">
          <div className="max-w-[1200px] mx-auto px-4 sm:px-6 lg:px-10">
            <div
              className="rounded-lg overflow-hidden border border-white/10 shadow-2xl"
              style={{ backgroundColor: "#0c1330" }}
            >
              <img
                src={BROCHURE_IMAGES[slug]}
                alt={`${b.title} brochure`}
                className="w-full h-auto block"
                loading="lazy"
              />
            </div>
            {BROCHURE_DOWNLOADS[slug] &&
              BROCHURE_DOWNLOADS[slug].length > 0 && (
                <div className="mt-4 flex flex-wrap gap-3 justify-end">
                  {BROCHURE_DOWNLOADS[slug].map((d, i) => (
                    <a
                      key={i}
                      href={d.href}
                      target="_blank"
                      rel="noopener noreferrer"
                      download
                      data-testid={`brochure-download-${i}`}
                      className="inline-flex items-center gap-2 rounded-full border px-4 py-2 text-xs font-semibold uppercase tracking-widest hover:text-white transition-colors"
                      style={{
                        borderColor: accent,
                        color: accent,
                      }}
                    >
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="14"
                        height="14"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        aria-hidden="true"
                      >
                        <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                        <polyline points="7 10 12 15 17 10" />
                        <line x1="12" y1="15" x2="12" y2="3" />
                      </svg>
                      <span>{d.label}</span>
                      {d.sizeMb && (
                        <span className="text-white/40 normal-case tracking-normal">
                          ({d.sizeMb} MB)
                        </span>
                      )}
                    </a>
                  ))}
                </div>
              )}
          </div>
        </section>
      )}

      {/* About + Advantages */}
      <section className="pb-14" data-testid="brochure-about">
        <div className="max-w-[1200px] mx-auto px-4 sm:px-6 lg:px-10 grid lg:grid-cols-3 gap-8">
          <div className="lg:col-span-2">
            <h2
              className="font-display font-extrabold text-white mb-4"
              style={{ fontSize: "clamp(1.3rem, 2.2vw, 1.75rem)" }}
            >
              {b.aboutTitle}
            </h2>
            <p className="text-white/80 leading-relaxed">{b.aboutBody}</p>
          </div>
          <div>
            <p
              className="text-[12px] font-bold tracking-[0.32em] uppercase mb-3"
              style={{ color: accent }}
            >
              {b.advantagesTitle}
            </p>
            <ul className="space-y-2.5">
              {b.advantages.map((a, i) => (
                <li
                  key={i}
                  className="flex items-start gap-2.5 text-white/90 text-sm"
                >
                  <Check
                    size={16}
                    style={{ color: accent }}
                    className="mt-0.5 shrink-0"
                  />
                  <span>{a}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* Categories */}
      <section className="bg-emka-2 py-14" data-testid="brochure-categories">
        <div className="max-w-[1200px] mx-auto px-4 sm:px-6 lg:px-10">
          <p
            className="text-[12px] font-bold tracking-[0.32em] uppercase"
            style={{ color: accent }}
          >
            {b.categoriesTitle}
          </p>
          <h2
            className="mt-2 font-display font-extrabold text-white leading-tight"
            style={{ fontSize: "clamp(1.4rem, 2.4vw, 2rem)" }}
          >
            {b.categoriesTitle}
          </h2>
          <span
            className="mt-4 inline-block h-[3px] w-14"
            style={{ backgroundColor: accent }}
          />
          <div className="mt-8 grid sm:grid-cols-2 lg:grid-cols-3 gap-3.5">
            {b.categories.map((c, i) => (
              <div
                key={i}
                className="rounded-md border border-white/10 p-4 flex items-start gap-3"
                style={{ backgroundColor: "#0c1330" }}
              >
                <Sparkles
                  size={16}
                  style={{ color: accent }}
                  className="mt-0.5 shrink-0"
                />
                <span className="text-white/90 text-sm leading-snug">{c}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Specs (heating only) */}
      {b.specs && (
        <section className="py-14" data-testid="brochure-specs">
          <div className="max-w-[1200px] mx-auto px-4 sm:px-6 lg:px-10">
            <p
              className="text-[12px] font-bold tracking-[0.32em] uppercase"
              style={{ color: accent }}
            >
              {b.specsTitle}
            </p>
            <div className="mt-6 grid sm:grid-cols-2 lg:grid-cols-3 gap-3.5">
              {b.specs.map((s, i) => (
                <div
                  key={i}
                  className="rounded-md border border-white/10 p-4 flex items-start gap-3"
                  style={{ backgroundColor: "#0c1330" }}
                >
                  <Shield
                    size={16}
                    style={{ color: accent }}
                    className="mt-0.5 shrink-0"
                  />
                  <span className="text-white/90 text-sm leading-snug">
                    {s}
                  </span>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* CTA */}
      <section className="bg-emka-2 py-14" data-testid="brochure-cta">
        <div className="max-w-[1200px] mx-auto px-4 sm:px-6 lg:px-10">
          <div
            className="rounded-md border border-white/10 p-8 sm:p-10 flex flex-col md:flex-row md:items-center md:justify-between gap-6"
            style={{ backgroundColor: "#0c1330" }}
          >
            <div>
              <h3 className="font-display font-extrabold text-white text-2xl sm:text-3xl">
                {b.ctaTitle}
              </h3>
              <p className="mt-2 text-white/75 max-w-xl">{b.ctaBody}</p>
            </div>
            <Link
              to="/contact"
              data-testid="brochure-cta-button"
              className="inline-flex items-center gap-3 px-6 py-3 font-semibold uppercase tracking-[0.18em] text-sm text-neutral-900"
              style={{ backgroundColor: "var(--emka-gold)" }}
            >
              {t.category.ctaButton}
              <ArrowRight size={16} />
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default BrochurePage;
